import { Platform, type PlatformType } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { cartFormB2bSchema, type CartFormValues } from '@cart-services/zod-schema/cartFormSchema'
import { CartFormProvider } from '@cart-ui/CartFormProvider/CartFormProvider'
import { type CountryCode, type ProjectCode } from '@constants'
import { zodResolver } from '@hookform/resolvers/zod'
import { type DeliveryClass } from '@hooks-api/types/dataModel/shipping'
import { createContext, useContext, useMemo, useState, type Dispatch, type PropsWithChildren, type SetStateAction } from 'react'
import { useForm } from 'react-hook-form'

export interface StmCartProviderProps {
  basePath: string
  countryCode: CountryCode
  projectCode: ProjectCode
  campaignId: number
  campaignCode: string
  currency?: string
}

export interface StmCartContextProps extends StmCartProviderProps {
  platform: PlatformType
  deliveryClass?: DeliveryClass
  isCartInfoValidating: boolean
  setIsCartInfoValidating: Dispatch<SetStateAction<boolean>>
}

const StmCartContext = createContext<StmCartContextProps | null>(null)

export const StmCartProvider = (props: PropsWithChildren<StmCartProviderProps>) => {
  const { children, basePath, countryCode, projectCode, campaignId, campaignCode, currency } = props
  const { items: cartItems } = useFpB2bCartService(campaignId, Platform.STM)
  const [isCartInfoValidating, setIsCartInfoValidating] = useState(false)
  const form = useForm<CartFormValues>({
    resolver: zodResolver(cartFormB2bSchema),
  })

  /**
   * 購物車目前常溫和冷凍是分開的，所以只取第一個項目就可，但未來應該要改成和 WP 官網一樣
   */
  const deliveryClass = cartItems[0]?.delivery_class

  const contextValue = useMemo(() => ({
    basePath,
    countryCode,
    projectCode,
    campaignId,
    campaignCode,
    currency,
    platform: Platform.STM as PlatformType,
    deliveryClass,
    isCartInfoValidating,
    setIsCartInfoValidating,
  }), [basePath, campaignCode, campaignId, countryCode, currency, deliveryClass, isCartInfoValidating, projectCode])

  return (
    <StmCartContext.Provider value={contextValue}>
      <CartFormProvider {...form}>
        {children}
      </CartFormProvider>
    </StmCartContext.Provider>
  )
}

export const useStmCart = () => {
  const context = useContext(StmCartContext)
  if (!context) throw new Error('useStmCart must be used within a StmCartProvider')
  return context
}
